import React, { useEffect, useState } from "react";
import { Files, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function BulkInvoiceDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState(null);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(null); setResult(null);
    api.get("/ledger/monthly-invoice/bulk-preview", { params: { month } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [open, month]);

  const pending = (rows || []).filter((r) => !r.already_invoiced && r.total > 0);
  const pendingTotal = pending.reduce((s, r) => s + r.total, 0);

  const run = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/ledger/monthly-invoice/bulk", { month });
      setResult(data);
      toast.success(`${data.created.length} fatura cariye işlendi`);
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="bulk-invoice-btn" className="rounded-full h-11 px-5 font-semibold gap-2">
        <Files className="h-4 w-4" /> Toplu Aylık Fatura
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Files className="h-5 w-5 text-primary" /> Toplu Aylık Fatura</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="bulk-invoice-month" className="font-mono w-48" disabled={!!result} />
            {!rows ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Hesaplanıyor…</div>
            ) : rows.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="bulk-invoice-empty">Bu ayda kişi sayısı kaydı olan müşteri yok.</div>
            ) : (
              <div className="rounded-lg border border-border overflow-hidden max-h-[50vh] overflow-y-auto" data-testid="bulk-invoice-list">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr><th className="px-3 py-2 text-left">Müşteri</th><th className="px-3 py-2 text-right">Kişi</th><th className="px-3 py-2 text-right">Tutar (KDV dahil)</th><th className="px-3 py-2 text-right">Durum</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const done = result?.created.some((c) => c.customer_id === r.customer_id);
                      return (
                        <tr key={r.customer_id} data-testid={`bulk-invoice-row-${r.customer_id}`} className="border-t border-border/60">
                          <td className="px-3 py-2 font-medium">{r.name}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.headcount}</td>
                          <td className="px-3 py-2 text-right font-mono">{TRY(r.total)}</td>
                          <td className="px-3 py-2 text-right text-xs">
                            {done ? <span className="text-emerald-400 inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" /> İşlendi</span>
                              : r.already_invoiced ? <span className="text-amber-400">Zaten faturalı</span>
                              : <span className="text-muted-foreground">Bekliyor</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {rows && rows.length > 0 && !result && (
              <div className="flex justify-between text-sm font-semibold" data-testid="bulk-invoice-summary">
                <span>{pending.length} müşteri faturalanacak</span><span className="font-mono text-primary">{TRY(pendingTotal)}</span>
              </div>
            )}
            {result && (
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm" data-testid="bulk-invoice-result">
                <b>{result.created.length}</b> fatura oluşturuldu · toplam <span className="font-mono">{TRY(result.total)}</span>
                {result.skipped.length > 0 && <span className="text-muted-foreground"> · {result.skipped.length} müşteri atlandı (zaten faturalı)</span>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{result ? "Kapat" : "İptal"}</Button>
            {!result && <Button onClick={run} disabled={saving || pending.length === 0} data-testid="bulk-invoice-save" className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {pending.length} Faturayı Cariye İşle
            </Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
